import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UI_TEXT } from '../config/gameConfig';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { audioManager } from '../utils/audioManager';

const VictoryScreen = ({ totalScore, onBackToMenu, onRestart }) => {
  const { isMobile } = useDeviceDetection();
  const [copied, setCopied] = useState(false);

  // Fade out and stop music when victory screen appears initially
  useEffect(() => {
    // We keep this to ensure music stops if the user lands here directly
    const initialStop = setTimeout(() => {
      if (!copied) {
        audioManager.stopMusic(2000); // 2 second fade out
      }
    }, 100);
    return () => clearTimeout(initialStop);
  }, []);

  const handleCopyPassword = () => {
    // 1. Stop music immediately upon copying
    audioManager.stopMusic(2000); // 2 second fade out

    // 2. Copy password
    navigator.clipboard.writeText(UI_TEXT.victory.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGoToWebsite = () => {
    window.open(UI_TEXT.victory.websiteUrl, '_blank');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white font-mono relative overflow-hidden py-8">
      {/* Animated Background Lines */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-green-500 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-green-500 to-transparent" />
      </div>

      <motion.div
        className={`w-full z-10 ${isMobile ? 'p-4 max-w-sm mx-auto' : 'max-w-3xl p-8'}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <div className={`text-center ${isMobile ? 'mb-6 space-y-3' : 'mb-12 space-y-4'}`}>
          <motion.h1
            className={`font-bold text-white tracking-tighter ${isMobile ? 'text-4xl' : 'text-6xl'}`}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {UI_TEXT.victory.header}
          </motion.h1>

          <motion.p
            className={`text-gray-300 ${isMobile ? 'text-base' : 'text-xl'}`}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {UI_TEXT.victory.subtitle}
          </motion.p>

          <motion.p
            className={`text-gray-400 italic ${isMobile ? 'text-sm' : 'text-base'}`}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {UI_TEXT.victory.message}
          </motion.p>
        </div>

        {/* Password Panel */}
        <motion.div
          className={`bg-[#111] border border-gray-800 relative ${isMobile ? 'p-4 mb-6' : 'p-8 mb-12'}`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {/* Decoration corners */}
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FFD900]" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FFD900]" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FFD900]" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FFD900]" />

          <div className={`text-gray-500 uppercase tracking-wider mb-3 text-center ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {UI_TEXT.victory.passwordLabel}
          </div>

          <motion.button
            onClick={handleCopyPassword}
            className={`w-full bg-[#FFD900]/10 border-2 border-[#FFD900] text-[#FFD900] font-bold uppercase tracking-wider hover:bg-[#FFD900] hover:text-black transition-all duration-300 ${isMobile ? 'py-3 text-base' : 'py-4 text-xl'}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {copied ? UI_TEXT.victory.passwordCopied : UI_TEXT.victory.password}
          </motion.button>

          <div className={`text-center text-gray-500 mt-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {UI_TEXT.victory.clickToCopy}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <div className="text-center space-y-4">
          <motion.button
            onClick={handleGoToWebsite}
            className={`bg-[#FFD900] text-black font-bold uppercase tracking-widest hover:bg-[#ffed4e] transition-all shadow-lg hover:shadow-[#FFD900]/50 ${isMobile ? 'px-6 py-3 text-sm w-full' : 'px-10 py-4'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {UI_TEXT.victory.websiteButton}
          </motion.button>

          <motion.button
            onClick={onRestart}
            className={`bg-transparent border-2 border-gray-700 text-gray-400 font-bold uppercase tracking-widest hover:border-gray-500 hover:text-white transition-all ${isMobile ? 'px-6 py-3 text-sm w-full' : 'px-10 py-4 mt-4'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {UI_TEXT.victory.playAgainButton}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default VictoryScreen;